const BOOTSTRAP_CONFIG_KEY = 'mirrorx_bootstrap_config_v2';
const LEGACY_BOOTSTRAP_CONFIG_KEY = 'mirrorx_bootstrap_config_v1';

const BOOTSTRAP_FLOW_DEFAULT = Object.freeze({
  domainVerified: false,
  templateDeployed: false,
  mirrorBound: false,
  navigationConfigured: false,
  lastUpdatedAt: '',
});

function normalizeTokenProfile(value, config = {}) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'bootstrap' || raw === 'runtime') {
    return raw;
  }

  const hasRuntimeToken = Boolean(String(config.cloudflareApiToken || '').trim());
  const onboardingCompleted = Boolean(config.onboardingCompleted);
  const provisionCompleted = Boolean(config.provisionCompleted);
  if (hasRuntimeToken && onboardingCompleted) return 'runtime';
  if (provisionCompleted && !hasRuntimeToken) return 'bootstrap';
  return '';
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || '').trim()).filter(Boolean);
}

function normalizePermissionAudit(value) {
  if (!value || typeof value !== 'object') return null;
  const profile = String(value.profile || '').trim().toLowerCase();
  return {
    profile: profile === 'bootstrap' || profile === 'runtime' ? profile : '',
    canProceed: Boolean(value.canProceed),
    requiredScopes: normalizeStringArray(value.requiredScopes),
    grantedScopes: normalizeStringArray(value.grantedScopes),
    missingScopes: normalizeStringArray(value.missingScopes),
    extraScopes: normalizeStringArray(value.extraScopes),
    riskLevel: String(value.riskLevel || '').trim().toLowerCase(),
    message: String(value.message || '').trim(),
  };
}

function normalizeDowngradeRecommendation(value) {
  if (!value || typeof value !== 'object') return null;
  return {
    shouldRotate: Boolean(value.shouldRotate),
    targetProfile: String(value.targetProfile || '').trim().toLowerCase(),
    recommendedScopes: normalizeStringArray(value.recommendedScopes),
    deadlineHours: Number(value.deadlineHours || 0),
    message: String(value.message || '').trim(),
  };
}

function normalizeBootstrapFlow(value, config = {}) {
  const source = value && typeof value === 'object' ? value : {};
  const inferredDomainVerified =
    String(config?.domainCheckResult?.selectedDeploymentStatus || '').trim().toLowerCase() === 'deployed' ||
    String(config?.domainCheckResult?.result || '').trim().toLowerCase() === 'site_deployed';

  return {
    domainVerified:
      source.domainVerified === true || source.domainVerified === false
        ? Boolean(source.domainVerified)
        : inferredDomainVerified,
    templateDeployed: Boolean(source.templateDeployed),
    mirrorBound: Boolean(source.mirrorBound),
    navigationConfigured: Boolean(source.navigationConfigured),
    lastUpdatedAt: String(source.lastUpdatedAt || '').trim(),
  };
}

function normalizeBootstrapFlowPatch(value) {
  const source = value && typeof value === 'object' ? value : {};
  const patch = {};
  if (source.domainVerified === true || source.domainVerified === false) {
    patch.domainVerified = Boolean(source.domainVerified);
  }
  if (source.templateDeployed === true || source.templateDeployed === false) {
    patch.templateDeployed = Boolean(source.templateDeployed);
  }
  if (source.mirrorBound === true || source.mirrorBound === false) {
    patch.mirrorBound = Boolean(source.mirrorBound);
  }
  if (source.navigationConfigured === true || source.navigationConfigured === false) {
    patch.navigationConfigured = Boolean(source.navigationConfigured);
  }
  return patch;
}

export function normalizeControlApiBase(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const parsed = new URL(withProtocol);
    const pathname =
      parsed.pathname && parsed.pathname !== '/'
        ? parsed.pathname.replace(/\/+$/, '')
        : '/api';
    return `${parsed.origin}${pathname}`;
  } catch {
    return '';
  }
}

function normalizeBootstrapConfig(config) {
  if (!config || typeof config !== 'object') return null;

  const domainCheckResult =
    config.domainCheckResult && typeof config.domainCheckResult === 'object'
      ? { ...config.domainCheckResult }
      : null;
  const permissionAudit = normalizePermissionAudit(config.permissionAudit);
  const downgradeRecommendation = normalizeDowngradeRecommendation(config.downgradeRecommendation);
  const bootstrapFlow = normalizeBootstrapFlow(config.bootstrapFlow, config);

  return {
    ...config,
    cloudflareApiToken: String(config.cloudflareApiToken || '').trim(),
    accountId: String(config.accountId || '').trim(),
    accountName: String(config.accountName || '').trim(),
    tokenStatus: String(config.tokenStatus || '').trim(),
    tokenVerifiedAt: String(config.tokenVerifiedAt || '').trim(),
    controlApiBase: normalizeControlApiBase(config.controlApiBase),
    zoneId: String(config.zoneId || '').trim(),
    databaseId: String(config.databaseId || '').trim(),
    kvConfigNamespaceId: String(config.kvConfigNamespaceId || '').trim(),
    kvCacheNamespaceId: String(config.kvCacheNamespaceId || '').trim(),
    shouldDeployTemplate: Boolean(config.shouldDeployTemplate),
    domainCheckCompleted: Boolean(config.domainCheckCompleted),
    domainCheckResult,
    onboardingCompleted: Boolean(config.onboardingCompleted),
    provisionCompleted: Boolean(config.provisionCompleted),
    controlApiBaseLocked: Boolean(config.controlApiBaseLocked),
    tokenProfile: normalizeTokenProfile(config.tokenProfile, config),
    tokenProfileUpdatedAt: String(config.tokenProfileUpdatedAt || '').trim(),
    permissionAudit,
    downgradeRecommendation,
    bootstrapFlow,
    updatedAt: String(config.updatedAt || ''),
  };
}

export function readBootstrapConfig() {
  try {
    const raw =
      globalThis.localStorage?.getItem(BOOTSTRAP_CONFIG_KEY) ||
      globalThis.localStorage?.getItem(LEGACY_BOOTSTRAP_CONFIG_KEY);
    if (!raw) return null;
    const parsed = normalizeBootstrapConfig(JSON.parse(raw));
    if (!parsed) return null;
    globalThis.localStorage?.setItem(BOOTSTRAP_CONFIG_KEY, JSON.stringify(parsed));
    return parsed;
  } catch {
    return null;
  }
}

export function isBootstrapReady(config = readBootstrapConfig()) {
  return Boolean(
    String(config?.cloudflareApiToken || '').trim() &&
      config?.controlApiBase &&
      config?.onboardingCompleted
  );
}

export function isBootstrapTokenProfile(config = readBootstrapConfig()) {
  return String(config?.tokenProfile || '').trim().toLowerCase() === 'bootstrap';
}

export function resetPlatformProvisionForReimport() {
  const current = readBootstrapConfig() || {};
  saveBootstrapConfig({
    ...current,
    cloudflareApiToken: '',
    controlApiBase: '',
    databaseId: '',
    kvConfigNamespaceId: '',
    kvCacheNamespaceId: '',
    accountId: '',
    accountName: '',
    tokenStatus: '',
    tokenVerifiedAt: '',
    provisionCompleted: false,
    controlApiBaseLocked: false,
    onboardingCompleted: false,
    tokenProfile: 'bootstrap',
    tokenProfileUpdatedAt: new Date().toISOString(),
    permissionAudit: null,
    downgradeRecommendation: null,
    permissionChecks: [],
    missingPermissions: [],
    routeCount: 0,
    bootstrapFlow: BOOTSTRAP_FLOW_DEFAULT,
  });
}

export function saveBootstrapConfig(config) {
  const current = readBootstrapConfig() || {};
  const payload = normalizeBootstrapConfig({
    ...current,
    ...config,
    updatedAt: new Date().toISOString(),
  });

  globalThis.localStorage?.setItem(BOOTSTRAP_CONFIG_KEY, JSON.stringify(payload));
  return payload;
}

export function getBootstrapFlowState(config = readBootstrapConfig()) {
  return normalizeBootstrapFlow(config?.bootstrapFlow, config);
}

export function updateBootstrapFlow(patch = {}, extraConfig = {}) {
  const current = readBootstrapConfig() || {};
  const currentFlow = normalizeBootstrapFlow(current.bootstrapFlow, current);
  const nextFlow = {
    ...currentFlow,
    ...normalizeBootstrapFlowPatch(patch),
    lastUpdatedAt: new Date().toISOString(),
  };
  return saveBootstrapConfig({
    ...extraConfig,
    bootstrapFlow: nextFlow,
  });
}

export function getBootstrapPublishReadiness(config = readBootstrapConfig()) {
  const flow = getBootstrapFlowState(config);
  const checks = [
    {
      key: 'domainVerified',
      label: 'Domain scan and deployment passed',
      done: Boolean(flow.domainVerified),
      route: '/mirror/source',
      action: 'Open Domain Scan',
    },
    {
      key: 'templateDeployed',
      label: 'Template deployed',
      done: Boolean(flow.templateDeployed),
      route: '/mirror/factory',
      action: 'Open Template Factory',
    },
    {
      key: 'mirrorBound',
      label: 'Mirror source bound',
      done: Boolean(flow.mirrorBound),
      route: '/mirror/editor',
      action: 'Open Visual Editor',
    },
    {
      key: 'navigationConfigured',
      label: 'Menu confirmed',
      done: Boolean(flow.navigationConfigured),
      route: '/mirror/navigation',
      action: 'Open Menu Editor',
    },
  ];

  const firstUnmet = checks.find((item) => !item.done) || null;
  return {
    ready: checks.every((item) => item.done),
    checks,
    firstUnmet,
    firstUnmetPath: firstUnmet?.route || '/mirror/source',
  };
}

export function clearBootstrapConfig() {
  globalThis.localStorage?.removeItem(BOOTSTRAP_CONFIG_KEY);
  globalThis.localStorage?.removeItem(LEGACY_BOOTSTRAP_CONFIG_KEY);
}
